import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import type {
  ImportStatRow,
  PlatformQueues,
  SignalInboxBacklog,
  SignalingDataTypeStats,
} from "../../../api/client.ts";
import { formatNumber } from "../../../utils/format.ts";
import Panel from "../../Panel.tsx";
import RelativeTime from "../../RelativeTime.tsx";
import Sparkline from "../../Sparkline.tsx";
import ScrollTable from "../../table/ScrollTable.tsx";
import { SIGNAL_STAT_DOMAINS, bySource, sourceLabel, statRows, sumOf } from "./importStats.ts";
import { DAY_MS, drainLabel, evaluationsNeeded, perSecond } from "./queueFormat.ts";
import SampledAt from "./SampledAt.tsx";

const numeric = { fontVariantNumeric: "tabular-nums" } as const;

interface Row {
  key: string;
  label: string;
  sources: string;
  records: number | null;
  added24h: number | null;
  waiting: number;
  watched: boolean;
  matched: number;
  matched24h: number;
  queue: SignalInboxBacklog | null;
}

/** One row per data type, from either side: dashboard_stats always has the four original types. */
function buildRows(
  signaling: SignalingDataTypeStats[],
  stats: ImportStatRow[] | null,
  queues: PlatformQueues | null,
): Row[] {
  const counted = new Map(signaling.map((s) => [s.data_type, s]));
  const queued = new Map((queues?.signal_inbox ?? []).map((q) => [q.data_type, q]));
  const types = [...new Set([...counted.keys(), ...queued.keys()])];

  return types.map((dataType) => {
    const s = counted.get(dataType);
    const q = queued.get(dataType) ?? null;
    const where = SIGNAL_STAT_DOMAINS[dataType];
    const imported = where && stats ? statRows(stats, where.domain, where.sources) : [];
    // dashboard_stats counts only the four original types; without import stats
    // the others have no record count to show.
    const hasCount = imported.length > 0 || !!s?.total_records || !!s?.records_added_24h;
    return {
      key: dataType,
      label: q?.label ?? s?.label ?? dataType,
      // A single source (Notion's "page") names nothing new.
      sources: bySource(imported.length > 1 || where?.domain !== "notion" ? imported : [])
        .slice(0, 3)
        .map((b) => sourceLabel(b.source))
        .join(" · "),
      records: imported.length ? sumOf(imported, "total") : hasCount ? (s?.total_records ?? 0) : null,
      added24h: imported.length ? sumOf(imported, "added_24h") : hasCount ? (s?.records_added_24h ?? 0) : null,
      // The sampled queue when there is one; dashboard_stats otherwise.
      waiting: q ? q.pending : (s?.inbox_pending ?? 0),
      watched: q ? q.active_workflows > 0 : !!s?.has_active_rules,
      matched: s?.signals_matched ?? 0,
      matched24h: s?.signals_matched_24h ?? 0,
      queue: q,
    };
  });
}

/**
 * Every record type the signal engine reads, as a pipeline: what came in, what
 * waits for the local model and how old it is, what goes in and out and when it
 * clears at the last hour's rate, the evaluations per second (one record decided
 * for one workflow) against what keeping up takes, and what matched a workflow. Records per source
 * come from the import stats; the queue is counted every five minutes and its
 * trend is the waiting count over the day.
 */
export default function SignalsPanel({
  signaling,
  stats,
  queues,
}: {
  signaling: SignalingDataTypeStats[];
  stats: ImportStatRow[] | null;
  queues: PlatformQueues | null;
}) {
  const rows = buildRows(signaling, stats, queues).sort(
    (a, b) => b.waiting - a.waiting || (b.records ?? 0) - (a.records ?? 0),
  );
  const waiting = rows.reduce((n, r) => n + r.waiting, 0);
  const out = rows.reduce((n, r) => n + (r.queue?.processed_1h ?? 0), 0);
  const incoming = rows.reduce((n, r) => n + (r.queue?.added_1h ?? 0), 0);
  const matched24h = rows.reduce((n, r) => n + r.matched24h, 0);

  const subtitle = [
    waiting ? `${formatNumber(waiting)} waiting for the model` : "Nothing waiting for the model",
    queues?.sampled_at ? `${formatNumber(out)}/h out · ${formatNumber(incoming)}/h in` : null,
    `${formatNumber(matched24h)} matched 24h`,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Panel
      title="Signals"
      subtitle={subtitle}
      action={queues ? <SampledAt at={queues.sampled_at} /> : undefined}
      count={rows.length}
      disablePadding
      fill
    >
      <ScrollTable>
        <TableHead>
          <TableRow>
            <TableCell>Type</TableCell>
            <TableCell align="right">Records</TableCell>
            <TableCell align="right">Waiting</TableCell>
            <TableCell>Oldest</TableCell>
            <TableCell align="right">Out / in per hour</TableCell>
            <TableCell align="right">Evaluations / s</TableCell>
            <TableCell align="right">Clears in</TableCell>
            <TableCell align="right">Matched</TableCell>
            <TableCell>Waiting, 24h</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={9} sx={{ color: "text.secondary" }}>
                No signal data types yet.
              </TableCell>
            </TableRow>
          )}
          {rows.map((r) => {
            const q = r.queue;
            const drain = q ? drainLabel(q.pending, q.hours_to_drain) : null;
            const stale = q?.oldest_pending_at && Date.now() - Date.parse(q.oldest_pending_at) > DAY_MS;
            const workflows = q?.active_workflows ?? 0;
            const needed = q ? evaluationsNeeded(q) : 0;
            const caption = [
              r.sources || null,
              !r.watched
                ? "No active workflow"
                : workflows
                  ? `${formatNumber(workflows)} workflow${workflows === 1 ? "" : "s"}`
                  : null,
              q?.skipped_24h ? `${formatNumber(q.skipped_24h)} skipped 24h` : null,
            ]
              .filter(Boolean)
              .join(" · ");
            return (
              <TableRow key={r.key} hover>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {r.label}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    {caption}
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={numeric}>
                  {r.records == null ? "–" : formatNumber(r.records)}
                  {!!r.added24h && (
                    <Typography variant="caption" component="div" sx={{ color: "text.secondary" }}>
                      +{formatNumber(r.added24h)} 24h
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right" sx={numeric}>
                  <Typography variant="body2" sx={{ ...numeric, color: r.waiting ? "text.primary" : "text.secondary" }}>
                    {formatNumber(r.waiting)}
                  </Typography>
                  {!!q?.in_flight && (
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      {formatNumber(q.in_flight)} in flight
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={{ whiteSpace: "nowrap", color: stale ? "warning.main" : undefined }}>
                  {q?.oldest_pending_at ? <RelativeTime value={q.oldest_pending_at} /> : "–"}
                </TableCell>
                <TableCell align="right" sx={{ ...numeric, whiteSpace: "nowrap" }}>
                  {q ? (
                    <Tooltip
                      title={`${formatNumber(q.processed_24h ?? 0)} out / ${formatNumber(q.added_24h ?? 0)} in over 24h`}
                    >
                      <span>
                        {formatNumber(q.processed_1h ?? 0)}
                        <Typography component="span" variant="caption" sx={{ color: "text.secondary" }}>
                          {" / "}
                          {formatNumber(q.added_1h ?? 0)}
                        </Typography>
                      </span>
                    </Tooltip>
                  ) : (
                    "–"
                  )}
                  {!!q?.failed_24h && (
                    <Typography variant="caption" component="div" sx={{ color: "error.main" }}>
                      {formatNumber(q.failed_24h)} failed 24h
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right" sx={{ ...numeric, whiteSpace: "nowrap" }}>
                  {q ? (
                    <Tooltip
                      title={`Last hour: ${formatNumber(q.evaluations_1h)} evaluations of ${formatNumber(q.records_evaluated_1h)} records · ${formatNumber(q.rules_1h)} by rules · ${formatNumber(q.screened_out_1h)} screened out · ${formatNumber(q.llm_evaluated_1h)} by the LLM · ${formatNumber(q.evaluations_24h)} in 24h`}
                    >
                      <span>{perSecond(q.evaluations_1h)}</span>
                    </Tooltip>
                  ) : (
                    "–"
                  )}
                  {needed > 0 && (
                    <Typography
                      variant="caption"
                      component="div"
                      sx={{ color: q && q.evaluations_1h < needed ? "warning.main" : "text.secondary" }}
                    >
                      needs {perSecond(needed)}
                    </Typography>
                  )}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ whiteSpace: "nowrap", color: drain?.growing ? "warning.main" : "text.secondary" }}
                >
                  {drain ? drain.text : "–"}
                </TableCell>
                <TableCell align="right" sx={numeric}>
                  {formatNumber(r.matched)}
                  {r.matched24h > 0 && (
                    <Typography variant="caption" component="div" sx={{ color: "success.main" }}>
                      +{formatNumber(r.matched24h)} 24h
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {q ? (
                    <Sparkline
                      variant="area"
                      showRange={false}
                      width={96}
                      height={24}
                      color={drain?.growing ? "var(--mui-palette-warning-main)" : undefined}
                      values={q.trend.map((p) => p.pending ?? 0)}
                    />
                  ) : (
                    "–"
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </ScrollTable>
    </Panel>
  );
}
